
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom'
import { MainMilestoneCard, SubMilestoneCard } from './components/MilestoneCard';
import type { MilestoneStatus, MainPackageContract, SubPackageContract, MainPackageContractNode } from './types';
import { usePayablesData } from "./hooks"
import clsx from 'clsx'

const Payables: React.FC = () => {
    const { projectID = "" } = useParams()
    const [searchParams] = useSearchParams();

    const [selectedMainPackageNode, setSelectedMainPackageNode] = useState<API.PROJECT.SalesContractNode>();
    const [selectedMainPackage, setSelectedMainPackage] = useState<MainPackageContract>();
    const [selectedSubPackage, setSelectedSubPackage] = useState<SubPackageContract>();
    
    // 用于滚动到激活的MainMilestoneCard
    const activeMainNodeRef = useRef<HTMLDivElement>(null);

    const { mainPackageContracts, subPackageContracts, links, loading } = usePayablesData(projectID);

    const isLinked = (subNode: API.PROJECT.ContractExpense) => {
        if(!selectedMainPackageNode) return false;
        return links.some(link => link.toCode === selectedMainPackageNode.pmCode && link.fromCode === subNode.paymentplancode)
    }
    const subNodeHasLink = (subNode: API.PROJECT.ContractExpense) => {
        return links.some(link => link.fromCode === subNode.paymentplancode)
    }
    const mainNodeHasLink = (mainNode: API.PROJECT.SalesContractNode) => {
        return links.some(link => link.toCode === mainNode.pmCode)
    }

    useEffect(() => {
        if(!mainPackageContracts || mainPackageContracts.length <= 0) return;
        if (!selectedMainPackage) {
            if (searchParams.get("type") === "main" && searchParams.get("code")) {
                const mainPackage = mainPackageContracts.find(m => m.code === searchParams.get("code"));
                if (!mainPackage) return;
                setSelectedMainPackageNode(mainPackage.nodes[0]);
                setSelectedMainPackage(mainPackage);
                return
            }
            setSelectedMainPackageNode(mainPackageContracts[0].nodes[0]);
            setSelectedMainPackage(mainPackageContracts[0]);
        }
    }, [mainPackageContracts]);

    useEffect(() => {
        if (!subPackageContracts || subPackageContracts.length <= 0) return;
        if (!selectedSubPackage) {
            if (searchParams.get("type") === "sub" && searchParams.get("code")) {
                const subPackage = subPackageContracts.find(s => s.code === searchParams.get("code"));
                if (!subPackage) return;
                setSelectedSubPackage(subPackage);
                return
            }
            setSelectedSubPackage(subPackageContracts[0]);
        }
    }, [subPackageContracts]);

    // 当选择的MainPackageNode变化时，滚动到对应的元素
    useEffect(() => {
        if (activeMainNodeRef.current) {
            // 使用setTimeout确保DOM更新完成后再滚动
            setTimeout(() => {
                activeMainNodeRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }, 100);
        }
    }, [selectedMainPackageNode?.pmCode]);


    const changeMainPackge = (mainPackage: MainPackageContract) => {
        let mainNode = mainPackage.nodes[0];
        setSelectedMainPackageNode(mainNode);
        setSelectedMainPackage(mainPackage);
        onChangeLinkedSubPackge(mainNode);
    }
    const onChnageMainPackageNode = (mainNode: MainPackageContractNode) => {
        setSelectedMainPackageNode(mainNode);
        onChangeLinkedSubPackge(mainNode);
    }

    const changeSubPackge = (subPackage: SubPackageContract) => {
        setSelectedSubPackage(subPackage);
    }
    const onChangeLinkedSubPackge = (mainPackageNode: MainPackageContractNode) => {
        if (!mainPackageNode) return;

        const subIsLinked = selectedSubPackage?.nodes.some(subNode => {
            return links.some(link => link.toCode === mainPackageNode.pmCode && link.fromCode === subNode.paymentplancode)
        })
        if(subIsLinked) return;

        const link = links.find(link => link.toCode === mainPackageNode.pmCode)
        if (!link) return;
        const subPackage = subPackageContracts.find(s => s.code === link.fromContractCode);
        if (!subPackage) return;
        setSelectedSubPackage(subPackage);
    }
    const subMilestoneOnLink = (subNode: API.PROJECT.ContractExpense) => {
        if (!subNode) return;
        const link = links.find(link => link.fromCode === subNode.paymentplancode)
        if (!link) return;
        const mainPackage = mainPackageContracts.find(m => m.code === link.toContractCode);
        if (!mainPackage) return;
        setSelectedMainPackage(mainPackage);
        let mainNode = null
        mainPackage.nodes.forEach(n => {
            if (n.pmCode === link.toCode) {
                mainNode = n
            }
        })
        if (!mainNode) return;
        setSelectedMainPackageNode(mainNode);
    }

    return (
        <div className="flex flex-col text-slate-900">
            <div className="flex-1 grid grid-cols-12 gap-4 mx-auto w-full">
                {/* Left Column: Main Contract Nodes */}
                <section className="col-span-6 flex flex-col" style={{ maxHeight: "calc(100vh - 140px)" }}>
                    <div className="shrink-0 p-2">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <span className="w-2 h-6 bg-blue-500 rounded-full"></span>
                                总包合同
                            </h2>
                        </div>
                        <div className='flex flex-wrap gap-2'>
                            {mainPackageContracts.map(m => (
                                <div key={m.code} onClick={() => changeMainPackge(m)} className={clsx("flex-1 h-12 rounded-xl border border-blue-200 bg-blue-50 flex items-center justify-center text-xs font-bold text-blue-700 shadow-sm",
                                    m.code === selectedMainPackage?.code ? "bg-blue-200" : "hover:bg-blue-100"
                                )}>
                                    {m.name}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 gap-2 flex flex-col">
                        {selectedMainPackage?.nodes.map(m => (
                            <MainMilestoneCard
                                key={m.pmCode}
                                hasLink={mainNodeHasLink(m)}
                                value={m}
                                isActive={selectedMainPackageNode?.pmCode === m.pmCode}
                                onClick={() => onChnageMainPackageNode(m)}
                                ref={selectedMainPackageNode?.pmCode === m.pmCode ? activeMainNodeRef : undefined}
                            />
                        ))}
                    </div>
                </section>

                {/* Right Column: Dynamic Relationships */}
                <section className="col-span-6 space-y-6 border-0 border-solid border-l-2 border-slate-200 pl-4 overflow-y-auto"
                    style={{ maxHeight: "calc(100vh - 140px)" }}>
                    <div className="bg-white overflow-hidden flex flex-col h-full">
                        <div className="shrink-0 pb-2">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                        <span className="w-2 h-6 bg-purple-600 rounded-full"></span>
                                        分包合同
                                    </h2>
                                </div>
                            </div>
                            <div className='max-h-[200px] overflow-auto'>
                                <div className='flex flex-wrap items-center gap-2'>
                                    {subPackageContracts.map(m => (
                                        <div key={m.code} onClick={() => changeSubPackge(m)}
                                            className={clsx("relative flex-1 px-2 py-3 whitespace-nowrap rounded-xl border border-purple-200 bg-purple-50 flex items-center justify-center text-xs font-bold text-purple-700 shadow-sm cursor-pointer",
                                                m.code === selectedSubPackage?.code ? "bg-purple-200 border-purple-500" : "hover:bg-purple-100"
                                            )}>
                                            {m.name}
                                            {m.hasCanPayment && (<div className='absolute right-0 top-0 scale-75 origin-top-right px-1 py-0.5 bg-red-500 text-white text-xs rounded'>有可付款项</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                        </div>
                        
                        <div className='flex-1 overflow-y-auto'>
                            <div className="p-2 bg-white relative">
                                <div className="gap-2 flex flex-col">
                                    {selectedSubPackage?.nodes.map(sm => (
                                        <SubMilestoneCard key={sm.paymentplancode} hasLink={subNodeHasLink(sm)} onLink={subMilestoneOnLink} value={sm} isActive={isLinked(sm)} />
                                    ))}
                                </div>
                            </div>
                            {!loading && subPackageContracts.length === 0 && (
                                <div className="flex flex-col items-center justify-center text-slate-400 py-12">
                                    <svg className="w-16 h-16 mb-4 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                                    <p className="text-lg font-medium">暂无分包合同数据</p>
                                </div>
                            )}
                        </div>
                        
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Payables;
